import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Fade,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  Button
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Language as WebsiteIcon,
  Search as AnalyzeIcon,
  Psychology as AIIcon,
  Error as ErrorIcon,
  AutoAwesome as AutoAwesomeIcon,
  TrendingUp as TrendingUpIcon,
  Groups as GroupIcon,
  Campaign as CampaignIcon,
  Schedule as ScheduleIcon,
  Palette as PaletteIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const ValueCard = styled(Card)(({ theme }) => ({
  height: '100%',
  textAlign: 'center',
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  }
}));

const FeatureChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  fontWeight: 600,
}));

const GenerationProgress = ({ 
  isAnalyzing, 
  websiteUrl, 
  error,
  currentStep = 0,
  mode = 'analyze', // 'analyze' or 'create'
  onComplete = null,
  onBack = null,
  onManualInput = null
}) => {
  const [educationStep, setEducationStep] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (isAnalyzing) {
      // Simulate progress
      const timer = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) return prev;
          return prev + Math.random() * 15;
        });
      }, 1000);

      // Rotate through education content
      const educationTimer = setInterval(() => {
        setEducationStep(prev => (prev + 1) % 3);
      }, 4000);

      return () => {
        clearInterval(timer);
        clearInterval(educationTimer);
      };
    } else if (!error) {
      setProgress(100);
    }
  }, [isAnalyzing, error]);

  const analysisSteps = mode === 'analyze' ? [
    {
      icon: <WebsiteIcon />,
      label: 'Scanning your website',
      description: 'Analyzing pages, content, and structure'
    },
    {
      icon: <AnalyzeIcon />,
      label: 'Extracting information',
      description: 'Identifying business details and brand voice'
    },
    {
      icon: <AIIcon />,
      label: 'Understanding your audience',
      description: 'Analyzing target market and messaging'
    }
  ] : [
    {
      icon: <CampaignIcon />,
      label: 'Planning your campaign',
      description: 'Strategizing content for multiple platforms'
    },
    {
      icon: <AIIcon />,
      label: 'Generating posts',
      description: 'Creating engaging content for each platform'
    },
    {
      icon: <ScheduleIcon />,
      label: 'Optimizing schedule',
      description: 'Setting up the perfect posting timeline'
    }
  ];

  const valueProps = [
    {
      icon: <TrendingUpIcon />,
      title: '10x Faster',
      description: 'Create months of content in minutes, not hours'
    },
    {
      icon: <GroupIcon />,
      title: 'Platform Optimized',
      description: 'Each post tailored for Twitter, Facebook, Instagram & LinkedIn'
    },
    {
      icon: <AutoAwesomeIcon />,
      title: 'AI-Powered',
      description: 'Smart content that resonates with your audience'
    }
  ];

  const educationContent = [
    {
      title: "We're analyzing your brand voice",
      description: "Our AI understands your unique tone and messaging to create authentic content that sounds like you.",
      features: ["Brand consistency", "Authentic voice", "Professional tone"]
    },
    {
      title: "Creating platform-specific content",
      description: "Each social platform has its own best practices. We optimize every post for maximum engagement.",
      features: ["Character limits", "Hashtag strategies", "Visual guidelines"]
    },
    {
      title: "Building your content calendar",
      description: "We'll generate 8-10 posts covering different angles to keep your audience engaged over time.",
      features: ["Diverse topics", "Engagement variety", "Strategic timing"]
    }
  ];

  if (error) {
    return (
      <Fade in>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <ErrorIcon color="error" sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Website Analysis Failed
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            We encountered an issue analyzing your website:
          </Typography>
          <Alert severity="error" sx={{ textAlign: 'left', mb: 3, maxWidth: 500, mx: 'auto' }}>
            {error}
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Don't worry! You have a couple of options to continue:
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            {onBack && (
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={onBack}
                sx={{ minWidth: 180 }}
              >
                Try Different URL
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={onManualInput}
              sx={{ minWidth: 180 }}
              disabled={!onManualInput}
            >
              Enter Details Manually
            </Button>
          </Box>
        </Box>
      </Fade>
    );
  }

  if (!isAnalyzing && !error) {
    return (
      <Fade in>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Avatar
            sx={{
              width: 80,
              height: 80,
              bgcolor: 'success.main',
              mx: 'auto',
              mb: 3
            }}
          >
            <CheckIcon sx={{ fontSize: 48 }} />
          </Avatar>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            {mode === 'analyze' ? 'Analysis Complete!' : 'Campaign Created!'}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {mode === 'analyze' 
              ? "We've successfully extracted information from your website."
              : "Your campaign has been created with personalized posts for each platform."
            }
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1, mb: 3 }}>
            {mode === 'analyze' ? (
              <>
                <FeatureChip 
                  icon={<CheckIcon />} 
                  label="Brand voice captured" 
                  color="success" 
                  variant="outlined"
                />
                <FeatureChip 
                  icon={<CheckIcon />} 
                  label="Target audience identified" 
                  color="success" 
                  variant="outlined"
                />
                <FeatureChip 
                  icon={<CheckIcon />} 
                  label="Content strategy ready" 
                  color="success" 
                  variant="outlined"
                />
              </>
            ) : (
              <>
                <FeatureChip 
                  icon={<CheckIcon />} 
                  label="8-10 posts generated" 
                  color="success" 
                  variant="outlined"
                />
                <FeatureChip 
                  icon={<CheckIcon />} 
                  label="Platform optimized" 
                  color="success" 
                  variant="outlined"
                />
                <FeatureChip 
                  icon={<CheckIcon />} 
                  label="Ready to schedule" 
                  color="success" 
                  variant="outlined"
                />
              </>
            )}
          </Box>
          {onComplete && mode === 'analyze' && (
            <Button
              variant="contained"
              size="large"
              onClick={onComplete}
              startIcon={<AutoAwesomeIcon />}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 700,
                boxShadow: theme => theme.customShadows?.button || theme.shadows[2],
              }}
            >
              Continue to Review
            </Button>
          )}
        </Box>
      </Fade>
    );
  }

  return (
    <Box sx={{ width: '100%', py: 4 }}>
      {/* Progress Section */}
      <Box sx={{ textAlign: 'center', mb: 5 }}>
        <Box sx={{ position: 'relative', display: 'inline-flex', mb: 3 }}>
          <CircularProgress 
            variant="determinate" 
            value={progress}
            size={80} 
            thickness={4}
            sx={{ color: 'primary.main' }}
          />
          <Box
            sx={{
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="h6" component="div" color="text.secondary">
              {Math.round(progress)}%
            </Typography>
          </Box>
        </Box>
        
        <Typography variant="h5" gutterBottom fontWeight="bold">
          {mode === 'analyze' ? 'Analyzing Your Website' : 'Creating Your Campaign'}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
          {websiteUrl}
        </Typography>
      </Box>

      {/* Linear Progress */}
      <Box sx={{ mb: 4, px: 3 }}>
        <LinearProgress 
          variant="determinate" 
          value={progress}
          sx={{ 
            height: 8, 
            borderRadius: 4,
            bgcolor: 'grey.200',
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
              background: theme => `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`
            }
          }} 
        />
      </Box>

      {/* Steps Progress */}
      <Paper sx={{ p: 3, mb: 4, bgcolor: 'background.default' }}>
        <Typography variant="subtitle1" gutterBottom sx={{ textAlign: 'center', mb: 3, fontWeight: 600 }}>
          What we're doing
        </Typography>
        <List sx={{ maxWidth: 600, mx: 'auto' }}>
          {analysisSteps.map((step, index) => {
            const isCompleted = progress > (index + 1) * 33;
            const isCurrent = progress > index * 33 && progress <= (index + 1) * 33;
            
            return (
              <ListItem 
                key={index}
                sx={{ 
                  opacity: isCompleted || isCurrent ? 1 : 0.5,
                  transition: 'all 0.5s ease'
                }}
              >
                <ListItemIcon>
                  <Avatar
                    sx={{
                      bgcolor: isCompleted ? 'success.main' : isCurrent ? 'primary.main' : 'action.disabled',
                      width: 36,
                      height: 36,
                      transition: 'all 0.5s ease'
                    }}
                  >
                    {isCompleted ? <CheckIcon /> : step.icon}
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography 
                      variant="body1" 
                      fontWeight={isCurrent ? 'bold' : 'normal'}
                      color={isCompleted ? 'success.main' : 'inherit'}
                    >
                      {step.label}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="body2" color="text.secondary">
                      {step.description}
                    </Typography>
                  }
                />
              </ListItem>
            );
          })}
        </List>
      </Paper>

      {/* Educational Content */}
      <Fade in key={educationStep} timeout={1000}>
        <Paper 
          sx={{ 
            p: 4, 
            mb: 4, 
            textAlign: 'center',
            background: theme => `linear-gradient(135deg, ${theme.palette.primary[50]} 0%, ${theme.palette.secondary[50]} 100%)`,
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom fontWeight="bold">
            {educationContent[educationStep].title}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
            {educationContent[educationStep].description}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1 }}>
            {educationContent[educationStep].features.map((feature, idx) => (
              <FeatureChip
                key={idx}
                label={feature}
                size="small"
                color="primary"
                variant="outlined"
              />
            ))}
          </Box>
        </Paper>
      </Fade>

      {/* Value Proposition */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', mb: 3 }}>
          Why Solo Social Campaigns Work
        </Typography>
        <Grid container spacing={3}>
          {valueProps.map((prop, index) => (
            <Grid item xs={12} sm={4} key={index}>
              <ValueCard>
                <CardContent>
                  <Avatar
                    sx={{
                      width: 56,
                      height: 56,
                      bgcolor: theme => theme.palette.primary[50],
                      color: 'primary.main',
                      mx: 'auto',
                      mb: 2
                    }}
                  >
                    {prop.icon}
                  </Avatar>
                  <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                    {prop.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {prop.description}
                  </Typography>
                </CardContent>
              </ValueCard>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          This typically takes 30-60 seconds • Please don't close this page
        </Typography>
      </Box>
    </Box>
  );
};

export default GenerationProgress; 